<template>
  <div class="vm-filter-card">
    <div class="vm-filter-card-header">
      <h4 class="vm-filter-card-title">Migration Filters</h4>
      <span v-if="loading" class="vm-filter-searching">Searching...</span>
    </div>

    <div class="vm-filter-grid">
      <div class="vm-filter-field">
        <label class="vm-filter-label">User</label>
        <select v-model="selectedUser" class="form-control vm-filter-native-select">
          <option v-for="option in users" :key="`user-${option.value || 'all'}`" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="vm-filter-field">
        <label class="vm-filter-label">VM State</label>
        <select v-model="selectedState" class="form-control vm-filter-native-select">
          <option v-for="option in states" :key="`state-${option.value || 'all'}`" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="vm-filter-field">
        <label class="vm-filter-label">HyperVisor</label>
        <select v-model="selectedHv" class="form-control vm-filter-native-select">
          <option v-for="option in hvs" :key="`hv-${option.value || 'all'}`" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="vm-filter-actions vm-filter-actions-inline">
        <button type="button" class="btn btn-default vm-filter-reset" @click="resetFilters">Reset Filters</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  endpoint: { type: String, required: true },
  initialUser: { type: [String, Number], default: '' },
  initialState: { type: [String, Number], default: '' },
  initialHv: { type: [String, Number], default: '' },
  userOptions: { type: Array, default: () => [] },
  stateOptions: { type: Array, default: () => [] },
  hvOptions: { type: Array, default: () => [] }
})

const selectedUser = ref(String(props.initialUser || ''))
const selectedState = ref(String(props.initialState || ''))
const selectedHv = ref(String(props.initialHv || ''))
const loading = ref(false)
let requestTimer = null

const users = computed(() => normalizeOptions(props.userOptions))
const states = computed(() => normalizeOptions(props.stateOptions))
const hvs = computed(() => normalizeOptions(props.hvOptions))

watch([selectedUser, selectedState, selectedHv], () => {
  scheduleFetch()
})

onBeforeUnmount(() => {
  window.clearTimeout(requestTimer)
})

function normalizeOptions(options) {
  return (options || []).map((option) => ({
    ...option,
    value: String(option.value || '')
  }))
}

function resetFilters() {
  selectedUser.value = ''
  selectedState.value = ''
  selectedHv.value = ''
}

function scheduleFetch() {
  window.clearTimeout(requestTimer)
  requestTimer = window.setTimeout(fetchResults, 180)
}

async function fetchResults() {
  loading.value = true

  const params = new URLSearchParams()
  if (selectedUser.value) params.append('search[user]', selectedUser.value)
  if (selectedState.value) params.append('search[state]', selectedState.value)
  if (selectedHv.value) params.append('search[hv]', selectedHv.value)

  const url = params.toString() ? `${props.endpoint}?${params.toString()}` : props.endpoint

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (!response.ok) throw new Error('Failed to update migration results.')

    const data = await response.json()
    const container = document.querySelector('#hypervisor-migrate-results')
    if (container && typeof data.results_html === 'string') {
      container.innerHTML = data.results_html
      if (window.sccMountVueComponents) {
        window.sccMountVueComponents()
      }
    }
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.vm-filter-card {
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid rgba(135, 156, 181, 0.28);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(244, 247, 251, 0.95));
  box-shadow: 0 16px 40px rgba(134, 151, 172, 0.16);
}

.vm-filter-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.vm-filter-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2e4661;
}

.vm-filter-searching {
  font-size: 12px;
  font-weight: 600;
  color: #69839e;
}

.vm-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 16px;
  align-items: end;
}

.vm-filter-native-select {
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(132, 152, 176, 0.34);
  background: rgba(248, 250, 253, 0.98);
  color: #314761;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.vm-filter-field { min-width: 0; }

.vm-filter-label {
  display: inline-block;
  margin-bottom: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7b8da3;
}

.vm-filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.vm-filter-reset {
  min-height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(125, 144, 167, 0.32);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(239, 244, 249, 0.98));
  color: #3e556f;
  font-weight: 600;
  box-shadow: 0 10px 24px rgba(142, 157, 179, 0.12);
}

.vm-filter-reset:hover {
  background: linear-gradient(180deg, rgba(247, 250, 253, 1), rgba(232, 238, 245, 1));
  color: #24384e;
}

.vm-search-select { position: relative; }

.vm-search-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
  width: 100%;
  padding: 0 14px;
  border: 1px solid rgba(135, 156, 181, 0.32);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #33485f;
  text-align: left;
}

.vm-search-select-trigger-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vm-search-select-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 30;
  padding: 12px;
  border: 1px solid rgba(135, 156, 181, 0.25);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 45px rgba(138, 154, 177, 0.22);
}

.vm-search-select-input { margin-bottom: 10px; }

.vm-search-select-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}

.vm-search-select-option {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: rgba(244, 247, 251, 0.92);
  color: #33485f;
  text-align: left;
}

.vm-search-select-option:hover,
.vm-search-select-option.is-selected {
  border-color: rgba(104, 142, 186, 0.28);
  background: rgba(227, 236, 247, 0.98);
}

@media (max-width: 991px) {
  .vm-filter-grid { grid-template-columns: 1fr; }
  .vm-filter-actions { justify-content: stretch; }
  .vm-filter-reset { width: 100%; }
}
</style>
